import { CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Modal, Space, Tag } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpenClass } from '../../openClasses/types/OpenClass';
import { certificateTypeLabel } from '../../openClasses/utils/certificateTypeLabel';
import {
  formatOpenClassDateRange,
  isOpenClassAssignable,
  offeredCertificateType,
  openClassesById,
} from '../../openClasses/utils/openClass';
import {
  ParticipantDraft,
  RegistrationDraft,
  newDraftKey,
} from '../utils/openClassParticipantDraft';
import OpenClassRegistrationModal from './OpenClassRegistrationModal';

type OpenClassParticipantRegistrationsModalProps = {
  canCreate: boolean;
  canDelete: boolean;
  canUpdate: boolean;
  draft: ParticipantDraft;
  onChange: (registrations: RegistrationDraft[]) => void;
  onClose: () => void;
  open: boolean;
  openClasses: OpenClass[];
};

const OpenClassParticipantRegistrationsModal: React.FC<
  OpenClassParticipantRegistrationsModalProps
> = ({
  canCreate,
  canDelete,
  canUpdate,
  draft,
  onChange,
  onClose,
  open,
  openClasses,
}) => {
  const { t } = useTranslation();
  const [editingRegistration, setEditingRegistration] = useState<
    RegistrationDraft | undefined
  >();
  const [addingRegistration, setAddingRegistration] = useState(false);
  const byId = useMemo(() => openClassesById(openClasses), [openClasses]);

  return (
    <>
      <Modal
        footer={<Button onClick={onClose}>{t('common.close')}</Button>}
        open={open}
        title={t('views.OpenClassBookingOrderDetails.manageOpenClasses', {
          name: draft.name,
        })}
        width={680}
        onCancel={onClose}>
        <div className="flex flex-col gap-3">
          {draft.registrations.length === 0 ? (
            <Empty
              description={t(
                'views.OpenClassBookingOrderDetails.noRegistrations',
              )}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            draft.registrations.map((registration) => {
              const openClass = byId.get(registration.openClassId);
              const editable =
                canUpdate && !!openClass && isOpenClassAssignable(openClass);

              return (
                <div
                  className="flex items-start justify-between gap-4 rounded-md border border-gray-200 p-3"
                  key={registration.key}>
                  <div className="min-w-0">
                    <div className="font-medium">
                      {openClass ? formatOpenClassDateRange(openClass) : '–'}
                    </div>
                    <div className="mt-1 text-sm text-blueGrayDark">
                      {openClass?.course.name ?? '–'}
                    </div>
                    <Space className="mt-2" size="small" wrap>
                      <Tag>
                        {certificateTypeLabel(registration.certificateType)}
                      </Tag>
                      <span className="text-xs text-blueGrayDark">
                        {t('views.OpenClassBookingOrderDetails.sessionCount', {
                          count: registration.sessionIds.length,
                        })}
                      </span>
                    </Space>
                  </div>
                  <Space>
                    {editable && (
                      <Button
                        aria-label={t('common.edit')}
                        icon={<EditOutlined />}
                        onClick={() => setEditingRegistration(registration)}
                      />
                    )}
                    {canDelete && (
                      <Button
                        aria-label={t('common.delete')}
                        icon={<CloseOutlined />}
                        type="text"
                        onClick={() =>
                          onChange(
                            draft.registrations.filter(
                              ({ key }) => key !== registration.key,
                            ),
                          )
                        }
                      />
                    )}
                  </Space>
                </div>
              );
            })
          )}
          {canCreate && (
            <Button
              className="self-start"
              icon={<PlusOutlined />}
              type="dashed"
              onClick={() => setAddingRegistration(true)}>
              {t('views.OpenClassBookingOrderDetails.addRegistration')}
            </Button>
          )}
        </div>
      </Modal>
      {(addingRegistration || editingRegistration) && (
        <OpenClassRegistrationModal
          open
          disabledOpenClassIds={draft.registrations
            .filter(({ key }) => key !== editingRegistration?.key)
            .map(({ openClassId }) => openClassId)}
          openClasses={openClasses}
          registration={editingRegistration}
          onClose={() => {
            setAddingRegistration(false);
            setEditingRegistration(undefined);
          }}
          onSave={(selection) => {
            const certificateType = offeredCertificateType(
              byId.get(selection.openClassId),
              selection.certificateTemplateId,
            );
            if (!certificateType) return;
            onChange(
              editingRegistration
                ? draft.registrations.map((registration) =>
                    registration.key === editingRegistration.key
                      ? { ...registration, ...selection, certificateType }
                      : registration,
                  )
                : [
                    ...draft.registrations,
                    { key: newDraftKey(), ...selection, certificateType },
                  ],
            );
          }}
        />
      )}
    </>
  );
};

export default OpenClassParticipantRegistrationsModal;
